import React, { useState } from 'react';
import { Container, Grid, Typography, Tab, Tabs, Paper, useTheme, Modal, Box, } from '@mui/material';

import { PulpPaperRoute, pulpPaperRoutesData, pulpPaperCategories } from '../../types';
import PulpPaperRouteCard from './PulpPaperRouteCard';
import PulpPaperFlowDiagram from './PulpPaperFlowDiagram';
import SwipeableFlowShell from '../common/SwipeableFlowShell';
import CategoryTabLabel from '../common/CategoryTabLabel';
import PulpPaperProcessDetail from './PulpPaperProcessDetail';

const PulpPaperView: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRoute, setSelectedRoute] = useState<PulpPaperRoute | null>(
  () => pulpPaperRoutesData.find((route) => route.category === pulpPaperCategories[0].category) ?? null
);
  const [showDetail, setShowDetail] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const newCategory = pulpPaperCategories[newValue];
    const firstRoute = pulpPaperRoutesData.find((route) => route.category === newCategory.category);
    setSelectedRoute(firstRoute ?? null);
    setShowDetail(false);
  };

  const handleRouteSelect = (route: PulpPaperRoute) => {
    setSelectedRoute(route);
    setShowDetail(false);
  };

  const handleShowDetail = () => {
    setShowDetail(true);
  };

  const handleCloseDetail = () => {
    setShowDetail(false);
  };

  const filteredRoutes = pulpPaperRoutesData.filter((route) => {
    const category = pulpPaperCategories[activeTab];
    return route.category === category.category;
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: isDarkMode ? '#1b5e20' : '#e8f5e9',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            color: isDarkMode ? '#66bb6a' : '#2e7d32',
            fontWeight: 600
          }}
        >
          制浆造纸技术路线
        </Typography>
        <Typography variant="body1" color="text.secondary">
          涵盖机械浆、化学浆、化机浆、废纸脱墨浆等制浆工艺，
          以及箱板纸、文化纸、白卡纸、卫生纸等造纸工艺，
          展示从原料到成品的全流程技术要点与关键参数。
        </Typography>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
            },
            '& .Mui-selected': {
              backgroundColor: isDarkMode ? '#2e7d3230' : '#e8f5e9',
            },
          }}
        >
          {pulpPaperCategories.map((category, categoryIndex) => (
            <Tab
              key={category.id}
              label={
                <CategoryTabLabel
                  label={category.label}
                  routes={
                    pulpPaperRoutesData
                      .filter((route) => route.category === category.category)
                      .map((route) => ({ id: route.id, name: route.name }))
                  }
                  selectedRouteId={selectedRoute?.id}
                  onSelectRoute={(routeId) => {
                    handleTabChange(null as unknown as React.SyntheticEvent, categoryIndex);
                    const target = pulpPaperRoutesData.find((route) => route.id === routeId);
                    if (target) setSelectedRoute(target);
                  }}
                />
              }
            />
          ))}
        </Tabs>
      </Paper>

      <Grid container spacing={3}>
        {selectedRoute && (
          <Grid item xs={12}>
            <SwipeableFlowShell
              routes={filteredRoutes}
              selectedId={selectedRoute.id}
              onSelect={(id) => setSelectedRoute(filteredRoutes.find((r) => r.id === id) ?? null)}
            >
              <PulpPaperFlowDiagram route={selectedRoute} onShowDetail={handleShowDetail} />
            </SwipeableFlowShell>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: isDarkMode ? '#66bb6a' : '#2e7d32',
              fontWeight: 600
            }}
          >
            {pulpPaperCategories[activeTab].label} - 技术路线
          </Typography>
          <Grid container spacing={2}>
            {filteredRoutes.map((route) => (
              <Grid item xs={12} sm={6} lg={4} key={route.id}>
                <PulpPaperRouteCard
                  route={route}
                  onClick={handleRouteSelect}
                  isSelected={selectedRoute?.id === route.id}
                />
              </Grid>
            ))}
          </Grid>
          {filteredRoutes.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5' }}>
              <Typography color="text.secondary">
                该分类下暂无技术路线数据
              </Typography>
            </Paper>
          )}
        </Grid>

      </Grid>

      <Modal
        open={showDetail && selectedRoute !== null}
        onClose={handleCloseDetail}
        aria-labelledby="process-detail-modal"
        aria-describedby="process-detail-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 900,
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
          }}
        >
          {selectedRoute && (
            <PulpPaperProcessDetail route={selectedRoute} onClose={handleCloseDetail} />
          )}
        </Box>
      </Modal>

      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 3,
          backgroundColor: isDarkMode ? '#4a3728' : '#fff3e0',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: isDarkMode ? '#ffb74d' : '#ff9800',
            fontWeight: 600
          }}
        >
          使用提示
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • 点击下方路线卡片查看详细工艺流程图
          <br />
          • 在流程图中点击工艺步骤可高亮显示当前步骤
          <br />
          • 点击流程节点右侧的 ℹ️ 图标可查看工艺详情
          <br />
          • 使用鼠标滚轮或控制按钮缩放流程图
          <br />• 拖动流程图可查看完整工艺流程
        </Typography>
      </Paper>
    </Container>
  );
};

export default PulpPaperView;
